import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { useCreateSkin } from "../api/assetlib";
import { ApiError } from "../api/client";
import { useMe } from "../api/me";
import {
  useModel,
  usePatchDressing,
  usePatchModel,
  useRestore,
  useSetParams,
} from "../api/models";
import type {
  AssetDetail,
  Dressing,
  ModelDetail,
  ModelDressing,
  ModelLatest,
  ModelSelection,
  ModelValues,
} from "../api/types";
import { artworkName } from "../assets/SaveLookDialog";
import { SectionHeader } from "../assets/SectionHeader";
import { SurfacesPanel } from "../assets/SurfacesPanel";
import { TrueRenderButton } from "../assets/TrueRenderButton";
import { Viewer, ViewerSlot, preloadViewer } from "../assets/inspector";
import { useDressingDetails } from "../assets/useDressingDetails";
import { creditsWord, rowMessageFor } from "../copy/errors";
import { ErrorLine } from "../ui/ErrorLine";
import { useNow } from "../ui/useNow";
import { NotFoundPage } from "../pages/NotFoundPage";
import { Input, PageHeader, Skeleton, Tabs } from "../ui";
import type { ViewerHandle, ViewerPick } from "../viewer/Viewer";
import type { SlotDoc } from "../viewer/math";
import { useAddToLibrary } from "./AddToLibrary";
import { STUDIO_EYEBROW } from "./DesignListPage";
import { DetailsPanel } from "./DetailsPanel";
import { HistoryPanel, isResize } from "./HistoryPanel";
import { InstructionBox } from "./InstructionBox";
import { GENERATING_NOTE, SculptedPanel } from "./SculptedPanel";
import { StateLine, isBuild } from "./StateLine";
import { TunePanel } from "./TunePanel";
import { StudioThreadBanner } from "./StudioThreadBanner";
import { useModelSocket } from "./useModelSocket";

import "./design.css";

export const NAME_MAX = 80;
/** A tuning change is sent this long after the last control was let go. */
export const TUNE_DEBOUNCE_MS = 400;
export const FIRST_STEP_WAIT =
  "Your design appears here in about 2 minutes. You can leave this page.";
export const REBUILDING = "Rebuilding…";

const NO_DEFAULTS: Pick<AssetDetail, "defaults"> = {
  defaults: { materials: {}, skins: {} },
};
const NO_DRESSING: ModelDressing = { materials: {}, skins: {}, colors_hex: {} };
const NO_POSES: Record<string, number> = {};
const LINE = "m-0 text-sm text-slate-700";

const live = (latest: ModelLatest | null): boolean =>
  latest !== null &&
  (latest.status === "queued" || latest.status === "running");

/** Whether the window is a phone's (under 768 px wide). */
function usePhone(): boolean {
  const [phone, setPhone] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setPhone(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return phone;
}

/** Whether the on-screen keyboard is open: the visible part of the window
 *  is under three quarters of its height. */
function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const onResize = () => setOpen(viewport.height < 0.75 * window.innerHeight);
    onResize();
    viewport.addEventListener("resize", onResize);
    return () => viewport.removeEventListener("resize", onResize);
  }, []);
  return open;
}

/** The design's name as the page's heading, editable in place: Enter or
 *  leaving the field saves, Esc cancels. */
function useName(detail: ModelDetail) {
  const patch = usePatchModel(detail.id);
  const [draft, setDraft] = useState<string | null>(null);
  // Esc closes the field, and leaving it then must not save.
  const dropped = useRef(false);
  const save = () => {
    if (dropped.current || draft === null) return;
    const name = draft.trim();
    setDraft(null);
    if (!name || name === detail.name) return;
    patch.mutate({ name });
  };
  const title: ReactNode =
    draft === null ? (
      detail.name
    ) : (
      <Input
        aria-label="Name"
        autoFocus
        value={draft}
        maxLength={NAME_MAX}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={save}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            save();
          } else if (event.key === "Escape") {
            dropped.current = true;
            setDraft(null);
          }
        }}
      />
    );
  const edit =
    draft === null ? (
      <button
        type="button"
        className="inline-flex min-h-[44px] items-center text-sm underline md:min-h-0"
        disabled={patch.isPending}
        aria-busy={patch.isPending ? true : undefined}
        onClick={() => {
          dropped.current = false;
          setDraft(detail.name);
        }}
      >
        Edit name
      </button>
    ) : null;
  return { title, edit, error: patch.error };
}

/** What stands where the 3D view will be while nothing is built yet
 *  ([design-studio §11.6]). */
function NothingBuilt(props: {
  detail: ModelDetail;
  now: number;
}): JSX.Element {
  const { detail } = props;
  const latest = detail.latest;
  const box = (
    <div className="viewer">
      <div className="viewer-box">
        <Skeleton width="100%" height="100%" />
      </div>
    </div>
  );
  if (latest === null || live(latest)) {
    const build = latest !== null && isBuild(latest);
    const sculpted = detail.engine === "mesh";
    return (
      <div className="flex flex-col gap-3" data-testid="first-step">
        {build ? (
          <p role="status" className={LINE}>
            {REBUILDING}
          </p>
        ) : (
          <>
            {latest ? (
              <StateLine
                latest={latest}
                estimate={detail.turn_estimate.first}
                now={props.now}
                createdAt={detail.revisions[0]?.created_at}
                note={sculpted ? GENERATING_NOTE : undefined}
              />
            ) : null}
            {/* A generation has no estimate: its state line says so. */}
            {sculpted ? null : <p className={LINE}>{FIRST_STEP_WAIT}</p>}
          </>
        )}
        {box}
      </div>
    );
  }
  // The first step did not succeed. A stopped step has no code.
  const sentence =
    latest.status === "cancelled" ? null : rowMessageFor(latest.error_code);
  const charge =
    latest.credits > 0
      ? `Charged ${creditsWord(latest.credits)} for the work done.`
      : sentence?.includes("Nothing was charged")
        ? null
        : "Nothing was charged.";
  return (
    <div
      role="alert"
      className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-4"
      data-testid="first-step"
    >
      {sentence ? <p className={LINE}>{sentence}</p> : null}
      {latest.status === "refused" && latest.refusal_reason ? (
        <p className={LINE}>{latest.refusal_reason}</p>
      ) : null}
      {charge ? <p className={LINE}>{charge}</p> : null}
    </div>
  );
}

function Design(props: {
  detail: ModelDetail;
  thinking: string | null;
  onExpired(): void;
}): JSX.Element {
  const { detail, thinking } = props;
  const { id, current, latest } = detail;
  const me = useMe();
  const stills = me.data?.features?.stills === true;
  const available = me.data?.balance.available ?? null;
  const phone = usePhone();
  const keyboard = useKeyboardOpen();
  const running = live(latest);
  const now = useNow(1_000, running);
  const working = detail.state === "working";
  const idle = detail.state === "idle";
  const name = useName(detail);
  // A sculpted design ([sculpted §5.2]): the same page without instructions,
  // sliders and surfaces. A change of its size is a rebuild, not a generation.
  const sculpted = detail.engine === "mesh";
  const newest = detail.revisions[0];
  const resizing =
    running && sculpted && newest !== undefined && isResize(newest);

  const [selection, setSelection] = useState<ModelSelection | null>(null);
  const viewerRef = useRef<ViewerHandle>(null);

  // ---- Surfaces: the page's own copy of what is on the current step. A
  // change made while a step runs is kept here and sent once the design is
  // idle again.
  const [dressing, setDressing] = useState<ModelDressing>(
    current?.dressing ?? NO_DRESSING,
  );
  const [unsent, setUnsent] = useState(false);
  const [surfaceError, setSurfaceError] = useState<unknown>(null);
  const patchDressing = usePatchDressing(id);
  const createSkin = useCreateSkin();
  const stored = current?.dressing ?? NO_DRESSING;
  const storedRef = useRef(stored);
  storedRef.current = stored;

  const shownN = useRef(current?.n ?? null);
  const slots = useMemo<SlotDoc[]>(
    () =>
      Array.isArray(current?.asset.meta.slots) ? current.asset.meta.slots : [],
    [current],
  );
  useEffect(() => {
    const n = current?.n ?? null;
    if (shownN.current === n) return;
    shownN.current = n;
    // Another step is shown: its surfaces replace the page's, unless a
    // change of the user's still waits to be sent.
    if (!unsent) setDressing(storedRef.current);
    setSelection((held) =>
      held && slots.some((slot) => slot.name === held.slot) ? held : null,
    );
  }, [current, slots, unsent]);

  const { mutate: sendDressing } = patchDressing;
  const send = useCallback(
    (next: ModelDressing) =>
      sendDressing(next, {
        onError: (error) => {
          // The row returns to what it showed.
          setSurfaceError(error);
          setDressing(storedRef.current);
        },
      }),
    [sendDressing],
  );
  useEffect(() => {
    if (!idle || !unsent) return;
    setUnsent(false);
    send(dressing);
  }, [idle, unsent, dressing, send]);

  const onSurfaces = async (next: Dressing) => {
    setSurfaceError(null);
    const skins: Record<string, string> = {};
    try {
      for (const [slot, value] of Object.entries(next.skins)) {
        if (typeof value === "string") {
          skins[slot] = value;
          continue;
        }
        // One of the user's images becomes artwork at once, then is put on.
        const doc = slots.find((s) => s.name === slot);
        const saved = await createSkin.mutateAsync({
          name: artworkName(value.name),
          upload_id: value.upload_id,
          region_type: doc?.region?.type ?? "flat",
          emissive: doc?.accepts.includes("screen") ?? false,
          ...(doc?.region ? { fit: { aspect: doc.region.aspect } } : {}),
        });
        skins[slot] = saved.id;
      }
    } catch (error) {
      setSurfaceError(error);
      return;
    }
    const mine = {
      materials: next.materials,
      skins,
      colors_hex: next.colors_hex,
    };
    setDressing(mine);
    setUnsent(true);
  };

  const worn = useMemo<Dressing>(
    () => ({ ...dressing, poses: NO_POSES }),
    [dressing],
  );
  const details = useDressingDetails(current?.asset ?? NO_DEFAULTS, worn);

  const asset = current?.asset;
  const assetId = asset?.id;
  const assetVersion = asset?.version;
  const assetMeta = asset?.meta;
  const assetFiles = asset?.files;
  const assetViewer = asset?.viewer;
  const subject = useMemo(
    () =>
      assetId !== undefined && assetMeta && assetFiles
        ? {
            kind: "shape" as const,
            detail: {
              id: assetId,
              version: assetVersion ?? 1,
              meta: assetMeta,
              files: assetFiles,
              viewer: assetViewer ?? "full",
            },
          }
        : null,
    [assetId, assetVersion, assetMeta, assetFiles, assetViewer],
  );

  const select = (slot: string | null) => {
    if (slot === null) return setSelection(null);
    const part = slots.find((s) => s.name === slot)?.part;
    setSelection(part && part !== "*" ? { slot, part } : { slot });
  };
  const onPick = (pick: ViewerPick) =>
    setSelection({
      slot: pick.slot,
      part: pick.part,
      point: pick.point,
      normal: pick.normal,
      view: pick.view,
    });

  // ---- Tune: a change is sent 400 ms after the last control was let go.
  const setParams = useSetParams(id);
  const { mutate: sendParams } = setParams;
  const [tuneError, setTuneError] = useState<unknown>(null);
  const [tuneReset, setTuneReset] = useState(0);
  const [tuning, setTuning] = useState(false);
  const waiting = useRef<ModelValues>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const onTune = (values: ModelValues) => {
    setTuneError(null);
    waiting.current = { ...waiting.current, ...values };
    setTuning(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const sent = waiting.current;
      waiting.current = {};
      sendParams(sent, {
        onError: (error) => {
          // The control goes back to the stored value.
          setTuneError(error);
          setTuneReset((n) => n + 1);
        },
        onSettled: () => setTuning(false),
      });
    }, TUNE_DEBOUNCE_MS);
  };
  // A build that did not succeed puts the controls back too.
  const failedBuild =
    latest !== null &&
    isBuild(latest) &&
    (latest.status === "failed" || latest.status === "cancelled")
      ? latest.n
      : null;
  const resetFor = useRef<number | null>(failedBuild);
  useEffect(() => {
    if (failedBuild === null || resetFor.current === failedBuild) return;
    resetFor.current = failedBuild;
    setTuneReset((n) => n + 1);
  }, [failedBuild]);

  // ---- Restore: never asks. A step whose files were cleaned up is rebuilt.
  const restore = useRestore(id);
  const [restoreError, setRestoreError] = useState<unknown>(null);
  const [rebuildN, setRebuildN] = useState<number | null>(null);
  const onRestore = (n: number) => {
    setRestoreError(null);
    restore.mutate(n, {
      onSuccess: (reply) => {
        if (reply.rebuilding) setRebuildN(reply.revision);
      },
      onError: setRestoreError,
    });
  };
  const restoring =
    rebuildN !== null &&
    !(latest !== null && latest.n >= rebuildN && !live(latest));
  const rebuilding =
    tuning ||
    restoring ||
    resizing ||
    (running && latest !== null && isBuild(latest));
  const buildFailed =
    latest !== null && isBuild(latest) && latest.status === "failed"
      ? rowMessageFor(latest.error_code)
      : null;

  // ---- The header: Add to Objects and True render, in a ⋯ menu on a
  // phone, where what comes of either is said under the header.
  const menu = useRef<HTMLDetailsElement>(null);
  const closeMenu = () => {
    if (menu.current) menu.current.open = false;
  };
  const library = useAddToLibrary(detail, dressing);
  const addButton = library.button(
    phone ? { onPressed: closeMenu } : undefined,
  );
  const [statusEl, setStatusEl] = useState<HTMLElement | null>(null);
  const currentN = current?.n ?? null;
  const stillTarget = useMemo(
    () => (currentN === null ? null : { model: id, revision: currentN }),
    [id, currentN],
  );
  const trueRender =
    stills && stillTarget ? (
      <TrueRenderButton
        target={stillTarget}
        dressing={dressing}
        poses={NO_POSES}
        viewerRef={viewerRef}
        statusTarget={phone ? statusEl : undefined}
        menu={phone ? { onPressed: closeMenu } : undefined}
      />
    ) : null;
  const actions = !phone ? (
    addButton || trueRender || library.status ? (
      <>
        {addButton || trueRender ? (
          <div className="flex flex-wrap items-start justify-end gap-3">
            {addButton}
            {trueRender}
          </div>
        ) : null}
        {library.status}
      </>
    ) : null
  ) : addButton || trueRender ? (
    <details ref={menu} className="relative">
      <summary
        className="flex h-11 w-11 cursor-pointer list-none items-center justify-center rounded-full bg-white text-lg font-bold leading-none text-slate-700 shadow-card"
        aria-label={`More actions for ${detail.name}`}
      >
        ⋯
      </summary>
      <div
        className="tb-card tb-card-plain absolute right-0 z-20 mt-1 flex w-64 flex-col gap-1 p-2 text-sm"
        role="menu"
      >
        {addButton}
        {trueRender}
      </div>
    </details>
  ) : null;

  // ---- The tabs. With nothing built there is nothing to tune or dress.
  const tabs = [
    ...(phone ? [{ id: "history", label: "History" }] : []),
    ...(current
      ? [
          { id: "tune", label: "Tune" },
          { id: "surfaces", label: "Surfaces" },
        ]
      : []),
    { id: "details", label: "Details" },
  ];
  const [tab, setTab] = useState<string | null>(null);
  const active = tabs.some((t) => t.id === tab) ? tab! : tabs[0].id;

  const history = (
    <div className="flex flex-col gap-3">
      <HistoryPanel
        revisions={detail.revisions}
        currentN={currentN}
        params={current?.params}
        disabled={!idle}
        onRestore={onRestore}
      />
      {restoreError ? (
        <ErrorLine error={restoreError} context="design" />
      ) : null}
      {buildFailed && latest?.source === "restore" ? (
        <p role="alert" className={LINE}>
          {buildFailed}
        </p>
      ) : null}
    </div>
  );

  return (
    <>
      <PageHeader
        eyebrow={STUDIO_EYEBROW}
        title={name.title}
        meta={name.edit}
        right={actions ?? undefined}
        className={phone ? "!flex-nowrap !items-start" : undefined}
      />
      {phone ? (
        <div
          role="status"
          data-testid="header-status"
          ref={setStatusEl}
          className="mb-2 flex flex-col gap-2 empty:hidden"
        />
      ) : null}
      {phone && statusEl && library.status
        ? createPortal(library.status, statusEl)
        : null}
      {library.dialog}
      {name.error ? (
        <div className="mb-4">
          <ErrorLine error={name.error} context="design" />
        </div>
      ) : null}
      <SectionHeader />
      {/* made for an ad's cast row (docs/SPEC.md §6.4) */}
      <StudioThreadBanner
        adId={detail.for_ad?.ad_id}
        adTitle={detail.ad_title}
        thing={detail.thing}
        design={detail}
      />
      <div className="grid items-start gap-4 md:grid-cols-[180px_minmax(0,1fr)_260px] md:grid-rows-[auto_1fr] lg:grid-cols-[220px_minmax(0,1fr)_320px] lg:gap-6">
        {phone ? null : (
          <aside
            className="md:col-start-1 md:row-span-2 md:row-start-1"
            data-testid="history-column"
          >
            {history}
          </aside>
        )}
        <div
          className="flex min-w-0 flex-col gap-3 md:col-start-2 md:row-start-1"
          data-testid="design-centre"
        >
          {current && subject ? (
            <>
              {running && latest && !resizing ? (
                <StateLine
                  latest={latest}
                  estimate={detail.turn_estimate.next}
                  now={now}
                  createdAt={detail.revisions[0]?.created_at}
                  note={sculpted ? GENERATING_NOTE : undefined}
                />
              ) : null}
              <div
                className="design-viewer"
                data-keyboard={keyboard ? "open" : undefined}
              >
                <ViewerSlot>
                  <Viewer
                    ref={viewerRef}
                    subject={subject}
                    dressing={worn}
                    details={details}
                    selectedSlot={sculpted ? null : (selection?.slot ?? null)}
                    marker={sculpted ? null : (selection?.point ?? null)}
                    dimmed={working}
                    overlay={
                      rebuilding ? (
                        <p role="status" className="design-over">
                          {REBUILDING}
                        </p>
                      ) : undefined
                    }
                    // A click in a sculpted design selects nothing.
                    onPick={sculpted ? undefined : onPick}
                    onExpired={props.onExpired}
                  />
                </ViewerSlot>
              </div>
            </>
          ) : (
            <NothingBuilt detail={detail} now={now} />
          )}
        </div>
        {sculpted ? (
          <SculptedPanel
            detail={detail}
            available={available}
            history={phone ? history : undefined}
          />
        ) : (
          <>
            <div
              className="min-w-0 md:col-start-3 md:row-span-2 md:row-start-1"
              data-testid="design-tabs"
            >
              <Tabs tabs={tabs} active={active} onChange={setTab}>
                {active === "history" ? history : null}
                {active === "tune" && current ? (
                  <div className="flex flex-col gap-3">
                    <TunePanel
                      key={tuneReset}
                      params={current.params}
                      values={current.values}
                      disabled={!idle}
                      onCommit={onTune}
                    />
                    {tuneError ? (
                      <ErrorLine error={tuneError} context="design" />
                    ) : null}
                    {buildFailed && latest?.source === "params" ? (
                      <p role="alert" className={LINE}>
                        {buildFailed}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {active === "surfaces" && asset ? (
                  <div className="flex flex-col gap-3">
                    <SurfacesPanel
                      detail={asset}
                      dressing={worn}
                      onChange={(next) => void onSurfaces(next)}
                      selectedSlot={selection?.slot ?? null}
                      onSelect={select}
                    />
                    {surfaceError ? (
                      <ErrorLine error={surfaceError} context="design" />
                    ) : null}
                  </div>
                ) : null}
                {active === "details" ? <DetailsPanel detail={detail} /> : null}
              </Tabs>
            </div>
            <div
              className={[
                "min-w-0 md:col-start-2 md:row-start-2",
                // On a phone the box stays at the bottom of the screen.
                "max-md:sticky max-md:bottom-0 max-md:z-10 max-md:-mx-4 max-md:border-t max-md:border-slate-200 max-md:bg-white max-md:px-4 max-md:py-3",
              ].join(" ")}
              data-testid="design-box"
            >
              <InstructionBox
                detail={detail}
                available={available}
                selection={selection}
                onClearSelection={() => setSelection(null)}
                thinking={thinking}
                compact={phone}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Loading() {
  return (
    <>
      <PageHeader eyebrow={STUDIO_EYEBROW} />
      <SectionHeader />
      <div
        className="grid items-start gap-6 md:grid-cols-[minmax(0,1fr)_320px]"
        aria-busy="true"
        data-testid="design-loading"
      >
        <div className="viewer">
          <div className="viewer-box">
            <Skeleton width="100%" height="100%" />
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <Skeleton width="100%" height={44} />
          <Skeleton width="100%" height={44} />
          <Skeleton width="100%" height={44} />
        </div>
      </div>
    </>
  );
}

/** `/assets/studio/:id`: one design ([design-studio §11.4–§11.8],
 *  [ux §8.17]): its history, the 3D view of the step that is shown with the
 *  state of the step in progress, the instruction box, and the tabs **Tune**,
 *  **Surfaces** and **Details**. A sculpted design ([sculpted §5.2],
 *  [ux §8.18]) has `SculptedPanel` in place of the box and the tabs. The socket keeps it fresh; while the socket
 *  is not open a busy design is asked for every 5 s. */
export function DesignPage() {
  const { id = "" } = useParams();
  // The page draws in 3D: its code starts on its way at once.
  useEffect(preloadViewer, []);
  const socket = useModelSocket(id);
  const model = useModel(id, { pollBusy: socket.status !== "open" });

  // The 3D view asks once for fresh addresses when one has run out.
  const asked = useRef(false);
  const shown = model.data?.current?.n;
  useEffect(() => {
    asked.current = false;
  }, [id, shown]);
  const { refetch } = model;
  const onExpired = useCallback(() => {
    if (asked.current) return;
    asked.current = true;
    void refetch();
  }, [refetch]);

  if (model.error instanceof ApiError && model.error.status === 404) {
    return <NotFoundPage />;
  }
  if (model.data) {
    return (
      <Design
        key={id}
        detail={model.data}
        thinking={socket.thinking}
        onExpired={onExpired}
      />
    );
  }
  if (model.isPending) return <Loading />;
  return (
    <>
      <PageHeader eyebrow={STUDIO_EYEBROW} />
      <SectionHeader />
      <ErrorLine error={model.error} context="design" />
    </>
  );
}
